/**
 * F-algebra and F-coalgebra diagrams: structure maps, initial
 * algebras, terminal coalgebras, and hylomorphisms.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { useId } from "react";
import { GRAY } from "../../../design-system/tokens.js";
import {
  DIAGRAM_MONO,
  STROKE,
  SUBTLE,
  ArrowDefs,
  ObjectNode,
  Arrow,
  FunctorBox,
} from "./primitives.js";

/**
 * F-Algebra diagram: F(A) — α →  A.
 *
 * Drawn left-to-right because algebras "absorb" structure: F is built up,
 * α collapses one F-layer.
 */
export function FAlgebraDiagram({
  carrier = "A",
  arrowLabel = "α",
  accent = STROKE,
  ariaLabel = "F-algebra arrow F(A) → A",
}: {
  readonly carrier?: string;
  readonly arrowLabel?: string;
  readonly accent?: string;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  const markerId = useId();
  return (
    <svg
      viewBox="0 0 240 90"
      role="img"
      aria-label={ariaLabel}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={markerId} color={accent} />
      <FunctorBox cx={48} cy={48} label={carrier} accent={accent} />
      <Arrow
        x1={76}
        y1={48}
        x2={180}
        y2={48}
        label={arrowLabel}
        markerId={markerId}
        color={accent}
      />
      <ObjectNode cx={196} cy={48} label={carrier} accent={accent} />
    </svg>
  );
}

/**
 * F-Coalgebra diagram: A — γ → F(A).
 *
 * Drawn left-to-right because coalgebras "observe" structure: γ exposes one
 * F-layer of behavior from a state.
 */
export function FCoalgebraDiagram({
  carrier = "S",
  arrowLabel = "γ",
  accent = STROKE,
  ariaLabel = "F-coalgebra arrow S → F(S)",
}: {
  readonly carrier?: string;
  readonly arrowLabel?: string;
  readonly accent?: string;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  const markerId = useId();
  return (
    <svg
      viewBox="0 0 240 90"
      role="img"
      aria-label={ariaLabel}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={markerId} color={accent} />
      <ObjectNode cx={44} cy={48} label={carrier} accent={accent} />
      <Arrow
        x1={60}
        y1={48}
        x2={164}
        y2={48}
        label={arrowLabel}
        markerId={markerId}
        color={accent}
      />
      <FunctorBox cx={194} cy={48} label={carrier} accent={accent} />
    </svg>
  );
}

/**
 * Initial F-algebra diagram: μF with its structure map `in : F(μF) → μF`,
 * Lambek's isomorphism annotation, and a unique catamorphism into a target
 * algebra (A, α).
 *
 * The picture is the categorical statement of "every algebra is reachable
 * from μF by exactly one fold":
 *
 *   F(μF) — in ─▶ μF
 *     |           | cata α (the UNIQUE algebra morphism)
 *   F(cata α)     |
 *     v           v
 *   F(A) ── α ──▶ A
 */
export function InitialAlgebraDiagram({
  accent = STROKE,
  targetAccent = SUBTLE,
  ariaLabel = "Initial F-algebra: μF with the unique catamorphism cata α to any target algebra (A, α)",
}: {
  readonly accent?: string;
  readonly targetAccent?: string;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  // Per-color arrowhead markers so the arrowhead fill tracks each arrow's
  // stroke color. ArrowDefs bakes the fill at definition time, so reusing
  // one marker across multiple stroke colors would leave the arrowhead in
  // the wrong color.
  const markerId = useId();
  const targetMarkerId = `${markerId}-target`;
  const subtleMarkerId = `${markerId}-subtle`;
  return (
    <svg
      viewBox="0 0 320 220"
      role="img"
      aria-label={ariaLabel}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={markerId} color={accent} />
      <ArrowDefs id={targetMarkerId} color={targetAccent} />
      <ArrowDefs id={subtleMarkerId} color={GRAY[700]} />
      <FunctorBox cx={64} cy={48} label="μF" accent={accent} />
      <ObjectNode cx={244} cy={48} label="μF" accent={accent} />
      <FunctorBox cx={64} cy={172} label="A" accent={targetAccent} />
      <ObjectNode cx={244} cy={172} label="A" accent={targetAccent} />
      {/* in : F(μF) → μF — Lambek's iso */}
      <Arrow
        x1={92}
        y1={48}
        x2={228}
        y2={48}
        label="in"
        subLabel="≅  (Lambek)"
        markerId={markerId}
        color={accent}
      />
      {/* α : F(A) → A */}
      <Arrow
        x1={92}
        y1={172}
        x2={228}
        y2={172}
        label="α"
        markerId={targetMarkerId}
        color={targetAccent}
      />
      {/* F(cata α) on the left */}
      <Arrow
        x1={64}
        y1={62}
        x2={64}
        y2={158}
        label="F(cata α)"
        markerId={subtleMarkerId}
        color={GRAY[700]}
      />
      {/* cata α on the right — emphasized as the unique morphism */}
      <Arrow
        x1={244}
        y1={62}
        x2={244}
        y2={158}
        label="cata α"
        subLabel="∃!"
        markerId={markerId}
        color={accent}
      />
      <text
        x={154}
        y={210}
        textAnchor="middle"
        fontSize="11"
        fontFamily={DIAGRAM_MONO}
        fill={GRAY[600]}
      >
        the unique algebra morphism into any (A, α)
      </text>
    </svg>
  );
}

/**
 * Terminal F-coalgebra diagram: νF with the unique anamorphism into it
 * from any source coalgebra (S, γ). Dual to InitialAlgebraDiagram.
 *
 *   S ── γ ─▶ F(S)
 *   |          |
 *   ana γ      F(ana γ)
 *   |          |
 *   v          v
 *   νF ── out ▶ F(νF)
 */
export function TerminalCoalgebraDiagram({
  accent = STROKE,
  sourceAccent = SUBTLE,
  ariaLabel = "Terminal F-coalgebra: νF with the unique anamorphism ana γ from any source coalgebra (S, γ)",
}: {
  readonly accent?: string;
  readonly sourceAccent?: string;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  // Per-color arrowhead markers — see InitialAlgebraDiagram for the
  // rationale.
  const markerId = useId();
  const sourceMarkerId = `${markerId}-source`;
  const subtleMarkerId = `${markerId}-subtle`;
  return (
    <svg
      viewBox="0 0 320 220"
      role="img"
      aria-label={ariaLabel}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={markerId} color={accent} />
      <ArrowDefs id={sourceMarkerId} color={sourceAccent} />
      <ArrowDefs id={subtleMarkerId} color={GRAY[700]} />
      <ObjectNode cx={64} cy={48} label="S" accent={sourceAccent} />
      <FunctorBox cx={244} cy={48} label="S" accent={sourceAccent} />
      <ObjectNode cx={64} cy={172} label="νF" accent={accent} />
      <FunctorBox cx={244} cy={172} label="νF" accent={accent} />
      {/* γ : S → F(S) */}
      <Arrow
        x1={78}
        y1={48}
        x2={216}
        y2={48}
        label="γ"
        markerId={sourceMarkerId}
        color={sourceAccent}
      />
      {/* out : νF → F(νF) — Lambek dual iso */}
      <Arrow
        x1={78}
        y1={172}
        x2={216}
        y2={172}
        label="out"
        subLabel="≅  (Lambek dual)"
        markerId={markerId}
        color={accent}
      />
      {/* ana γ — unique morphism on the left */}
      <Arrow
        x1={64}
        y1={62}
        x2={64}
        y2={158}
        label="ana γ"
        subLabel="∃!"
        markerId={markerId}
        color={accent}
      />
      {/* F(ana γ) on the right */}
      <Arrow
        x1={244}
        y1={62}
        x2={244}
        y2={158}
        label="F(ana γ)"
        markerId={subtleMarkerId}
        color={GRAY[700]}
      />
      <text
        x={154}
        y={210}
        textAnchor="middle"
        fontSize="11"
        fontFamily={DIAGRAM_MONO}
        fill={GRAY[600]}
      >
        the unique coalgebra morphism into the terminal νF
      </text>
    </svg>
  );
}

/**
 * Functor-lift diagram — shows that F transports morphisms.
 *
 * A state machine carries a state s₀ with no way to transport a function
 * f : A → B along the transitions. An F-algebra lives inside a functor F,
 * and F(f) : F(A) → F(B) is derived automatically. The diagram shows the
 * "lift":
 *
 *   A ─────────── f ─────────▶ B
 *   │                          │
 *   │  apply F                 │  apply F
 *   v                          v
 *  F(A) ────── F(f) ─────▶ F(B)
 *
 * The four-node commuting square is the functor law: F(id) = id and
 * F(g ∘ f) = F(g) ∘ F(f).
 */
export function FunctorLiftDiagram({
  accent = STROKE,
  ariaLabel = "Functor lift: morphism f from A to B lifts to F(f) from F(A) to F(B)",
}: {
  readonly accent?: string;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  const markerId = useId();
  const applyMarkerId = `${markerId}-apply`;
  return (
    <svg
      viewBox="0 0 360 230"
      role="img"
      aria-label={ariaLabel}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={markerId} color={accent} />
      <ArrowDefs id={applyMarkerId} color={GRAY[600]} />
      {/* Top row: A → B */}
      <ObjectNode cx={64} cy={52} label="A" accent={accent} />
      <ObjectNode cx={296} cy={52} label="B" accent={accent} />
      <Arrow x1={78} y1={52} x2={282} y2={52} label="f" markerId={markerId} color={accent} />
      {/* Bottom row: F(A) → F(B) */}
      <FunctorBox cx={64} cy={178} label="A" accent={accent} />
      <FunctorBox cx={296} cy={178} label="B" accent={accent} />
      <Arrow x1={94} y1={178} x2={270} y2={178} label="F(f)" markerId={markerId} color={accent} />
      {/* Left: A → F(A) — "apply F" label matches the doc-comment ASCII art */}
      <Arrow
        x1={64}
        y1={66}
        x2={64}
        y2={164}
        label="apply F"
        markerId={applyMarkerId}
        color={GRAY[600]}
      />
      {/* Right: B → F(B) — "apply F" label matches the doc-comment ASCII art */}
      <Arrow
        x1={296}
        y1={66}
        x2={296}
        y2={164}
        label="apply F"
        markerId={applyMarkerId}
        color={GRAY[600]}
      />
      <text
        x={180}
        y={218}
        textAnchor="middle"
        fontSize="11"
        fontFamily={DIAGRAM_MONO}
        fill={GRAY[600]}
      >
        F(g ∘ f) = F(g) ∘ F(f) — the functor law
      </text>
    </svg>
  );
}

/**
 * Hylomorphism fusion diagram: a single recursive pass that unfolds with
 * γ and folds with α in one step, without materializing the intermediate
 * structure.
 *
 *   S ── γ ─▶ F(S) ── F(hylo) ─▶ F(A) ── α ─▶ A
 *   ╰──────────────── hylo α γ ──────────────╯
 */
export function HylomorphismDiagram({
  accent = STROKE,
  algebraAccent,
  coalgebraAccent,
  ariaLabel = "Hylomorphism hylo α γ : S → A factors as γ then F(hylo) then α — and the law hylo α γ ≡ α ∘ F(hylo) ∘ γ",
}: {
  readonly accent?: string;
  readonly algebraAccent?: string;
  readonly coalgebraAccent?: string;
  readonly ariaLabel?: string;
}): React.JSX.Element {
  // Per-color arrowhead markers so each colored arrow lands a matching
  // arrowhead — ArrowDefs bakes the fill at definition time.
  const markerId = useId();
  const algMarkerId = `${markerId}-alg`;
  const coalgMarkerId = `${markerId}-coalg`;
  const subtleMarkerId = `${markerId}-subtle`;
  const fusedMarkerId = `${markerId}-fused`;
  const algC = algebraAccent ?? accent;
  const coalgC = coalgebraAccent ?? accent;
  return (
    <svg
      viewBox="0 0 520 170"
      role="img"
      aria-label={ariaLabel}
      style={{ display: "block", maxWidth: "100%", height: "auto" }}
    >
      <ArrowDefs id={algMarkerId} color={algC} />
      <ArrowDefs id={coalgMarkerId} color={coalgC} />
      <ArrowDefs id={subtleMarkerId} color={GRAY[700]} />
      <ArrowDefs id={fusedMarkerId} color={accent} />
      <ObjectNode cx={36} cy={86} label="S" accent={coalgC} />
      <FunctorBox cx={156} cy={86} label="S" accent={coalgC} />
      <FunctorBox cx={336} cy={86} label="A" accent={algC} />
      <ObjectNode cx={484} cy={86} label="A" accent={algC} />
      <Arrow
        x1={50}
        y1={86}
        x2={132}
        y2={86}
        label="γ"
        subLabel="unfold"
        markerId={coalgMarkerId}
        color={coalgC}
      />
      <Arrow
        x1={184}
        y1={86}
        x2={312}
        y2={86}
        label="F(hylo α γ)"
        markerId={subtleMarkerId}
        color={GRAY[700]}
      />
      <Arrow
        x1={364}
        y1={86}
        x2={470}
        y2={86}
        label="α"
        subLabel="fold"
        markerId={algMarkerId}
        color={algC}
      />
      <Arrow
        x1={40}
        y1={76}
        x2={480}
        y2={76}
        label="hylo α γ"
        subLabel="single pass, no intermediate F(μF) materialized"
        markerId={fusedMarkerId}
        color={accent}
        variant="dashed"
        curve={48}
      />
    </svg>
  );
}
